<script setup>
/**
 * Teams.
 *
 * Archetype E, matching Agents: shell header with `Create new team`, then the
 * shared table. The create and edit wizards keep their own stepped layout -
 * they are a flow, not a list, and stay on the legacy wrapper.
 */
import { useAlert } from 'dashboard/composables';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { computed, ref } from 'vue';
import { picoSearch } from '@scmmishra/pico-search';
import { useMapGetter } from 'dashboard/composables/store.js';
import { useStoreGetters, useStore } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';

import SettingsPageShell from 'dashboard/components-next/Settings/SettingsPageShell.vue';
import SettingsSearchInput from 'dashboard/components-next/Settings/SettingsSearchInput.vue';
import {
  BaseTable,
  BaseTableRow,
  BaseTableCell,
} from 'dashboard/components-next/table';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ConfirmDialog from 'dashboard/components-next/dialog/ConfirmDialog.vue';
import Skeleton from 'dashboard/components-next/skeleton/Skeleton.vue';
import EmojiIcon from 'dashboard/components-next/emoji-icon-picker/EmojiIcon.vue';

const store = useStore();
const { t } = useI18n();
const getters = useStoreGetters();
const { isAdmin } = useAdmin();

const loading = ref({});
const searchQuery = ref('');

const teamsList = useMapGetter('teams/getTeams');

const filteredTeamsList = computed(() => {
  const query = searchQuery.value.trim();
  if (!query) return teamsList.value;
  return picoSearch(teamsList.value, query, ['name', 'description']);
});

const uiFlags = computed(() => getters['teams/getUIFlags'].value);

const tableHeaders = computed(() => [
  t('TEAMS_SETTINGS.LIST.NAME'),
  t('TEAMS_SETTINGS.LIST.DESCRIPTION'),
  t('TEAMS_SETTINGS.LIST.ACTIONS'),
]);

const noDataMessage = computed(() =>
  searchQuery.value
    ? t('TEAMS_SETTINGS.NO_RESULTS')
    : t('TEAMS_SETTINGS.LIST.404')
);

const deleteTeam = async ({ id }) => {
  try {
    loading.value[id] = true;
    await store.dispatch('teams/delete', id);
    useAlert(t('TEAMS_SETTINGS.DELETE.API.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(t('TEAMS_SETTINGS.DELETE.API.ERROR_MESSAGE'));
  } finally {
    loading.value[id] = false;
  }
};

const showDeletePopup = ref(false);
const selectedTeam = ref({});

const openDelete = team => {
  showDeletePopup.value = true;
  selectedTeam.value = team;
};

const closeDelete = () => {
  showDeletePopup.value = false;
  selectedTeam.value = {};
};

const confirmDeletion = () => {
  deleteTeam(selectedTeam.value);
  closeDelete();
};

const deleteConfirmText = computed(
  () => `${t('TEAMS_SETTINGS.DELETE.CONFIRM.YES')} ${selectedTeam.value.name}`
);

const deleteRejectText = computed(() => t('TEAMS_SETTINGS.DELETE.CONFIRM.NO'));

const confirmDeleteTitle = computed(() =>
  t('TEAMS_SETTINGS.DELETE.CONFIRM.TITLE', {
    teamName: selectedTeam.value.name,
  })
);

const confirmPlaceHolderText = computed(() =>
  t('TEAMS_SETTINGS.DELETE.CONFIRM.PLACE_HOLDER', {
    teamName: selectedTeam.value.name,
  })
);
</script>

<template>
  <SettingsPageShell
    wide
    :title="$t('TEAMS_SETTINGS.HEADER')"
    :description="$t('TEAMS_SETTINGS.DESCRIPTION')"
  >
    <template v-if="isAdmin" #actions>
      <router-link :to="{ name: 'settings_teams_new' }">
        <Button :label="$t('TEAMS_SETTINGS.NEW_TEAM')" size="sm" />
      </router-link>
    </template>

    <div class="flex items-center justify-between gap-3">
      <SettingsSearchInput
        v-model="searchQuery"
        :placeholder="$t('TEAMS_SETTINGS.SEARCH_PLACEHOLDER')"
      />
      <span v-if="teamsList.length" class="text-sm text-n-slate-11">
        {{ $t('TEAMS_SETTINGS.COUNT', { n: teamsList.length }) }}
      </span>
    </div>

    <div
      v-if="uiFlags.isFetching"
      role="status"
      aria-busy="true"
      :aria-label="$t('TEAMS_SETTINGS.LOADING')"
      class="flex flex-col gap-5"
    >
      <Skeleton
        v-for="row in 4"
        :key="row"
        variant="text"
        :lines="2"
        :announce="false"
      />
    </div>

    <BaseTable
      v-else
      :headers="tableHeaders"
      :items="filteredTeamsList"
      :no-data-message="noDataMessage"
    >
      <template #row="{ items }">
        <BaseTableRow v-for="team in items" :key="team.id" :item="team">
          <template #default>
            <BaseTableCell>
              <div class="flex items-center gap-3 py-1">
                <div
                  class="flex items-center justify-center flex-shrink-0 rounded-xl outline outline-1 outline-n-weak -outline-offset-1 size-8"
                >
                  <EmojiIcon
                    v-if="team.icon"
                    :value="team.icon"
                    :color="team.icon_color"
                    class="size-4"
                  />
                  <Icon
                    v-else
                    icon="i-lucide-users-round"
                    class="size-4 text-n-slate-11"
                  />
                </div>
                <span
                  class="block truncate text-body-main text-n-slate-12 capitalize"
                >
                  {{ team.name }}
                </span>
              </div>
            </BaseTableCell>

            <BaseTableCell>
              <span class="block truncate text-body-main text-n-slate-11">
                {{ team.description }}
              </span>
            </BaseTableCell>

            <BaseTableCell align="end" class="w-24">
              <div v-if="isAdmin" class="flex justify-end gap-3 flex-shrink-0">
                <router-link
                  :to="{
                    name: 'settings_teams_edit',
                    params: { teamId: team.id },
                  }"
                >
                  <Button
                    v-tooltip.top="$t('TEAMS_SETTINGS.LIST.EDIT_TEAM')"
                    icon="i-woot-settings"
                    slate
                    sm
                  />
                </router-link>

                <Button
                  v-tooltip.top="$t('TEAMS_SETTINGS.DELETE.BUTTON_TEXT')"
                  icon="i-woot-bin"
                  slate
                  sm
                  class="hover:enabled:text-n-ruby-11 hover:enabled:bg-n-ruby-2"
                  :is-loading="loading[team.id]"
                  @click="openDelete(team)"
                />
              </div>
            </BaseTableCell>
          </template>
        </BaseTableRow>
      </template>
    </BaseTable>

    <ConfirmDialog
      v-if="showDeletePopup"
      v-model:show="showDeletePopup"
      :title="confirmDeleteTitle"
      :description="$t('TEAMS_SETTINGS.DELETE.CONFIRM.MESSAGE')"
      :confirm-button-label="deleteConfirmText"
      :cancel-button-label="deleteRejectText"
      :confirm-value="selectedTeam.name"
      :confirm-placeholder="confirmPlaceHolderText"
      @confirm="confirmDeletion"
      @close="closeDelete"
    />
  </SettingsPageShell>
</template>
